import React, { useState } from 'react';
import { Button, Spinner, Alert, Input, FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { registerPasskey } from '../../services/passkeyService';
import { isWebAuthnSupported, getWebAuthnErrorMessage } from '../../utils/webauthn';

const PasskeyRegistration = ({ onSuccess, buttonText = 'Agregar Passkey', buttonProps = {} }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [nickname, setNickname] = useState('');

  const handleRegisterClick = () => {
    setError(null);
    setSuccess(null);

    // Check browser support
    if (!isWebAuthnSupported()) {
      setError('Las passkeys no son compatibles con este navegador. Por favor usa Chrome, Safari o Edge.');
      return;
    }

    // Show nickname input modal
    setShowNicknameModal(true);
  };

  const handleConfirmRegistration = async () => {
    setShowNicknameModal(false);
    setLoading(true);

    try {
      // Register passkey with optional nickname
      const result = await registerPasskey(nickname.trim());

      setSuccess(`Passkey "${result.nickname || 'Sin nombre'}" registrada exitosamente!`);

      // Clear nickname for next registration
      setNickname('');

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      console.error('Passkey registration error:', err);
      const errorMessage = getWebAuthnErrorMessage(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNickname = () => {
    setShowNicknameModal(false);
    setNickname('');
  };

  return (
    <div className="passkey-registration">
      {error && (
        <Alert color="danger" className="mb-3" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" className="mb-3" toggle={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Button
        color="primary"
        onClick={handleRegisterClick}
        disabled={loading || !isWebAuthnSupported()}
        {...buttonProps}
      >
        {loading ? (
          <>
            <Spinner size="sm" className="me-2" />
            Registrando...
          </>
        ) : (
          <>
            <span className="me-2">🔐</span>
            {buttonText}
          </>
        )}
      </Button>

      {!isWebAuthnSupported() && (
        <small className="text-danger d-block mt-2">
          Las passkeys no son compatibles con este navegador
        </small>
      )}

      {/* Nickname Input Modal */}
      <Modal isOpen={showNicknameModal} toggle={handleCancelNickname}>
        <ModalHeader toggle={handleCancelNickname}>
          Nombra tu Passkey
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            Dale un apodo a esta passkey para ayudarte a identificarla más tarde (ej., "Mi iPhone", "Laptop del Trabajo").
          </p>
          <FormGroup>
            <Label for="passkey-nickname">Apodo (opcional)</Label>
            <Input
              type="text"
              id="passkey-nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="ej., Mi iPhone"
              maxLength={50}
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleConfirmRegistration();
                }
              }}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancelNickname}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleConfirmRegistration}>
            Continuar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PasskeyRegistration;
